import {
  catalogDataSchema,
  catalogAttestation,
  publishedCatalogSchema,
  emptyCatalogData,
  DEFAULT_CATALOG,
  type PublishedCatalog,
} from '@/features/compliance/catalog/catalogModel'
import { PDF_SOURCE, SOURCE_ATTESTATION } from './sourceQuestionnaire'
import { z } from 'zod'

import { isStoredPng } from '@/features/compliance/demo/showModeArtifacts'
import {
  createMeasurementSchema,
  toMeasurementValues,
} from '@/features/compliance/schemas/measurementSchema'
import type { ExclusionCatalog, MeasurementSubmission } from '@/features/compliance/types'

export const SHOW_CATALOG_V1 = {
  formVersionId: '1',
  versionLabel: 'Demonstrationsfassung 1 · 15.10.2026',
  questions: [
    {
      id: 'medical-advice',
      label: 'Wurde Ihnen ärztlich empfohlen, vor einer Behandlung Rücksprache zu halten?',
      required: true,
    },
    {
      id: 'current-complaints',
      label: 'Bestehen aktuell Beschwerden, die Sie vor der Behandlung besprechen möchten?',
      required: true,
    },
    {
      id: 'health-change',
      label: 'Gab es zuletzt gesundheitliche Veränderungen, die berücksichtigt werden sollen?',
      required: true,
    },
    {
      id: 'uncertainty',
      label: 'Bestehen Unklarheiten zur Verträglichkeit der geplanten Behandlung?',
      required: true,
    },
  ],
} satisfies ExclusionCatalog

// Keep historical definitions when adding a version; never reinterpret stored answers.
export const SHOW_CATALOG = PDF_SOURCE satisfies ExclusionCatalog

export const SHOW_MEASUREMENT_RULES = { unit: 'cm', decimalPlaces: 1, minimum: '1', maximum: '300' }
export const SHOW_ATTESTATION_V1 =
  'Ich habe die oben aufgeführten Angaben geprüft und bestätige sie mit meiner Unterschrift. Dies ist eine Demonstration mit Beispieldaten.'
export const SHOW_ATTESTATION = SOURCE_ATTESTATION
const SUPPORTED_SHOW_CATALOGS = [
  { catalog: SHOW_CATALOG_V1, attestation: SHOW_ATTESTATION_V1 },
  { catalog: SHOW_CATALOG, attestation: SHOW_ATTESTATION },
] as const

const text = z.string().trim().min(1)
// Signed wording must survive validation unchanged, including accepted surrounding whitespace.
const signedText = z.string().refine((value) => value.trim().length > 0)
const scopeSchema = z.object({ locationId: text, customerId: text })
const actorSchema = z.object({ id: text, name: text })
const time = z.iso.datetime()
const phaseSchema = z.object({
  upper: z.string().optional(),
  middle: z.string().optional(),
  lower: z.string().optional(),
})
const valuesSchema = z.object({ before: phaseSchema, after: phaseSchema })
export const createQuestionnaireSchema = (catalog: PublishedCatalog) =>
  z.object({
    formData: catalogDataSchema(catalog),
    contraindicationsSignature: z.string().optional(),
    answers: z
      .array(z.enum(['YES', 'NO'], { error: 'Bitte beantworten Sie jede Frage.' }))
      .length(catalog.questions.length),
    medications: z.literal(''),
    allergies: z.literal(''),
    notes: z.literal(''),
  })
export const questionnaireSchema = createQuestionnaireSchema(DEFAULT_CATALOG)
export const signatureSchema = z
  .string()
  .max(500000)
  .refine(isStoredPng, 'Die Unterschrift ist beschädigt. Bitte zeichnen Sie sie erneut.')
const snapshotSchema = z.object({
  catalog: publishedCatalogSchema.optional(),
  formData: z.record(z.string(), z.string()).optional(),
  contraindicationsSignature: z.string().optional(),
  id: text,
  customerId: text,
  customerName: text,
  locationId: text,
  locationName: text,
  timezone: text,
  actor: actorSchema,
  signedAt: time,
  version: text,
  questions: z
    .array(z.object({ id: text, label: signedText, answer: z.enum(['YES', 'NO']) }))
    .min(1),
  medications: z.string(),
  allergies: z.string(),
  notes: z.string(),
  attestation: signedText,
  signature: signatureSchema,
})
const documentSchema = snapshotSchema
  .extend({
    pdf: z.string().regex(/^data:application\/pdf;base64,[A-Za-z0-9+/]+={0,2}$/),
  })
  .superRefine((document, context) => {
    const version = SUPPORTED_SHOW_CATALOGS.find(
      (item) => item.catalog.versionLabel === document.version,
    )
    const catalog = document.catalog ?? version?.catalog
    if (
      document.catalog &&
      (document.catalog.formVersionId === '2' ||
        document.catalog.locationId !== document.locationId ||
        document.catalog.versionLabel !== document.version)
    )
      context.addIssue({ code: 'custom', message: 'Der Vorlagenkontext stimmt nicht überein.' })
    if (document.version === SHOW_CATALOG.versionLabel || document.catalog) {
      const fields = catalogDataSchema(document.catalog ?? DEFAULT_CATALOG).safeParse(
        document.formData,
      )
      if (!fields.success)
        context.addIssue({ code: 'custom', message: 'Ungültige medizinische Angaben.' })
      if (document.medications || document.allergies || document.notes)
        context.addIssue({ code: 'custom', message: 'Unbekannte Zusatzangaben.' })
      if (
        !document.formData ||
        (!document.questions.some((q) => q.answer === 'YES') &&
          !signatureSchema.safeParse(document.contraindicationsSignature).success) ||
        (document.questions.some((q) => q.answer === 'YES') &&
          document.contraindicationsSignature !== '')
      )
        context.addIssue({
          code: 'custom',
          message: 'Unvollständige Angaben oder widersprüchliche Unterschrift.',
        })
    } else if (
      document.formData !== undefined ||
      document.contraindicationsSignature !== undefined
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Unbekannte Felder in einer historischen Anamnese.',
      })
    }
    if (
      !catalog ||
      document.questions.length !== catalog.questions.length ||
      document.questions.some(
        (question, index) =>
          question.id !== catalog.questions[index]?.id ||
          question.label !== catalog.questions[index]?.label,
      ) ||
      document.attestation !==
        (document.catalog ? catalogAttestation(document.catalog) : version?.attestation)
    )
      context.addIssue({
        code: 'custom',
        message: 'Unbekannte oder unvollständige Anamnesefassung.',
      })
  })
const releaseSchema = z.object({ submissionId: text, reason: text, actor: actorSchema, at: time })
const noteSnapshotSchema = z.object({ id: text, text, actor: actorSchema, at: time })
const noteSchema = noteSnapshotSchema.extend({ history: z.array(noteSnapshotSchema).optional() })
const measurementSchema = valuesSchema.extend({
  revision: z.number().int().positive(),
  reason: z.string(),
  actor: actorSchema,
  at: time,
})
const treatmentSchema = z.object({
  id: text,
  appointmentId: text.optional(),
  submissionId: text,
  startedAt: time,
  completedAt: time.optional(),
  actor: actorSchema,
  notes: z.array(noteSchema),
  measurements: z.array(measurementSchema),
})
export const showRecordSchema = z
  .object({
    version: z.literal(1),
    scope: scopeSchema,
    revision: z.number().int().nonnegative(),
    documents: z.array(documentSchema),
    releases: z.array(releaseSchema),
    treatments: z.array(treatmentSchema),
  })
  .superRefine((record, context) => {
    const invalid = () =>
      context.addIssue({ code: 'custom', message: 'Widersprüchlicher Speicherstand.' })
    const documents = new Map(record.documents.map((document) => [document.id, document]))
    if (
      documents.size !== record.documents.length ||
      record.documents.some(
        (document) =>
          document.customerId !== record.scope.customerId ||
          document.locationId !== record.scope.locationId,
      )
    )
      invalid()
    const releases = new Set(record.releases.map((release) => release.submissionId))
    if (
      releases.size !== record.releases.length ||
      record.releases.some(
        (release) =>
          !documents
            .get(release.submissionId)
            ?.questions.some((question) => question.answer === 'YES'),
      )
    )
      invalid()
    if (
      new Set(record.treatments.map((item) => item.id)).size !== record.treatments.length ||
      new Set(record.treatments.flatMap((item) => (item.appointmentId ? [item.appointmentId] : [])))
        .size !== record.treatments.filter((item) => item.appointmentId).length ||
      record.treatments.filter((item) => !item.completedAt).length > 1
    )
      invalid()
    for (const treatment of record.treatments) {
      const document = documents.get(treatment.submissionId)
      if (
        !document ||
        (document.questions.some((question) => question.answer === 'YES') &&
          !releases.has(document.id)) ||
        (!treatment.completedAt && treatment.submissionId !== record.documents.at(-1)?.id) ||
        new Set(treatment.notes.map((note) => note.id)).size !== treatment.notes.length
      )
        invalid()
      treatment.measurements.forEach((measurement, index) => {
        const phase = (values: z.infer<typeof phaseSchema>) => ({
          upper: values.upper ?? '',
          middle: values.middle ?? '',
          lower: values.lower ?? '',
        })
        if (
          measurement.revision !== index + 1 ||
          !createMeasurementSchema(
            SHOW_MEASUREMENT_RULES,
            index ? 'correction' : 'capture',
          ).safeParse({
            before: phase(measurement.before),
            after: phase(measurement.after),
            correctionReason: measurement.reason,
          }).success
        )
          invalid()
      })
    }
  })
export type ShowScope = z.infer<typeof scopeSchema>
export type ShowActor = z.infer<typeof actorSchema>
export type Questionnaire = z.infer<typeof questionnaireSchema>
export type SigningSnapshot = z.infer<typeof snapshotSchema>
export type ShowDocument = z.infer<typeof documentSchema>
export type ShowRecord = z.infer<typeof showRecordSchema>
export type ShowTreatment = z.infer<typeof treatmentSchema>
export interface ShowContext extends ShowScope {
  actor: ShowActor
  role: string
  locationName: string
  timezone: string
}

export function emptyShowRecord(scope: ShowScope): ShowRecord {
  return {
    version: 1,
    scope: scopeSchema.parse(scope),
    revision: 0,
    documents: [],
    releases: [],
    treatments: [],
  }
}

export function showClearance(record: ShowRecord): 'missing' | 'review' | 'ready' {
  record = showRecordSchema.parse(record)
  const latest = record.documents.at(-1)
  if (!latest) return 'missing'
  if (!latest.questions.some((question) => question.answer === 'YES')) return 'ready'
  return record.releases.some((release) => release.submissionId === latest.id) ? 'ready' : 'review'
}

export type ShowAction =
  | { type: 'sign'; document: ShowDocument }
  | { type: 'release'; submissionId: string; reason: string }
  | { type: 'start' }
  | { type: 'note'; treatmentId: string; text: string }
  | { type: 'edit-note'; treatmentId: string; noteId: string; text: string }
  | { type: 'measurement'; treatmentId: string; values: MeasurementSubmission }
  | { type: 'complete'; treatmentId: string }

/** Demo transitions only; these are not the server's medical or credit gate. */
export function applyShowAction(
  record: ShowRecord,
  action: ShowAction,
  context: ShowContext,
  at: string,
  id: string,
): ShowRecord {
  const next = showRecordSchema.parse(record)
  if (
    context.customerId !== record.scope.customerId ||
    context.locationId !== record.scope.locationId
  )
    throw new Error('Der Kundenkontext hat sich geändert.')
  if (action.type === 'sign') {
    const document = documentSchema.parse(action.document)
    if (
      document.customerId !== context.customerId ||
      document.locationId !== context.locationId ||
      document.actor.id !== context.actor.id
    )
      throw new Error('Der Dokumentkontext stimmt nicht überein.')
    if (next.documents.some((item) => item.id === document.id))
      throw new Error('Dieses Dokument wurde bereits gespeichert.')
    if (next.treatments.some((item) => !item.completedAt))
      throw new Error('Bitte schließen Sie zuerst die laufende Behandlung ab.')
    next.documents.push(document)
  } else if (action.type === 'release') {
    if (context.role !== 'branch_manager')
      throw new Error('Nur die Filialleitung kann diese Freigabe dokumentieren.')
    if (showClearance(record) !== 'review' || record.documents.at(-1)?.id !== action.submissionId)
      throw new Error('Die Anamnese hat sich geändert. Bitte laden Sie die Ansicht neu.')
    const reason = action.reason.trim()
    if (!reason || reason.length > 3000)
      throw new Error('Bitte dokumentieren Sie die Rücksprache mit höchstens 3000 Zeichen.')
    next.releases.push({ submissionId: action.submissionId, reason, actor: context.actor, at })
  } else if (action.type === 'start') {
    if (showClearance(record) !== 'ready')
      throw new Error('Eine unterschriebene und freigegebene Anamnese ist erforderlich.')
    if (next.treatments.some((item) => !item.completedAt))
      throw new Error('Es gibt bereits eine laufende Behandlung.')
    next.treatments.push({
      id,
      submissionId: next.documents.at(-1)!.id,
      startedAt: at,
      actor: context.actor,
      notes: [],
      measurements: [],
    })
  } else {
    const treatment = next.treatments.find((item) => item.id === action.treatmentId)
    if (!treatment) throw new Error('Die Behandlung wurde nicht gefunden.')
    if (action.type === 'measurement') {
      const previous = treatment.measurements.at(-1)
      if (
        previous
          ? action.values.expectedRevision !== previous.revision
          : action.values.expectedRevision !== undefined
      )
        throw new Error('Die Messwerte wurden bereits geändert. Bitte laden Sie die Ansicht neu.')
      const phase = (values: MeasurementSubmission['before']) => ({
        upper: values.upper ?? '',
        middle: values.middle ?? '',
        lower: values.lower ?? '',
      })
      const input = createMeasurementSchema(
        SHOW_MEASUREMENT_RULES,
        previous ? 'correction' : 'capture',
      ).parse({
        before: phase(action.values.before),
        after: phase(action.values.after),
        correctionReason: action.values.correctionReason ?? '',
      })
      treatment.measurements.push({
        ...toMeasurementValues(input),
        revision: (previous?.revision ?? 0) + 1,
        reason: input.correctionReason.trim(),
        actor: context.actor,
        at,
      })
    } else {
      if (action.type === 'edit-note') {
        const note = treatment.notes.find((item) => item.id === action.noteId)
        if (!note) throw new Error('Die Notiz wurde nicht gefunden.')
        const value = action.text.trim()
        if (!value || value.length > 3000)
          throw new Error('Bitte geben Sie eine Notiz mit höchstens 3000 Zeichen ein.')
        const previous = { id: note.id, text: note.text, actor: note.actor, at: note.at }
        note.history = [...(note.history ?? []), previous]
        note.text = value
        note.actor = context.actor
        note.at = at
      } else if (action.type === 'note') {
        const value = action.text.trim()
        if (!value || value.length > 3000)
          throw new Error('Bitte geben Sie eine Notiz mit höchstens 3000 Zeichen ein.')
        treatment.notes.push({ id, text: value, actor: context.actor, at })
      } else {
        if (treatment.completedAt) throw new Error('Diese Behandlung ist bereits abgeschlossen.')
        treatment.completedAt = at
      }
    }
  }
  next.revision += 1
  return showRecordSchema.parse(next)
}

/** Copy only the same catalog; old answer positions must never become different medical answers. */
export function questionnaireForCorrection(
  document: ShowDocument,
  catalog: PublishedCatalog = DEFAULT_CATALOG,
): Questionnaire {
  return {
    answers:
      document.version === catalog.versionLabel ? document.questions.map((q) => q.answer) : [],
    formData:
      document.version === catalog.versionLabel && document.formData
        ? { ...document.formData }
        : emptyCatalogData(catalog, document.customerName),
    medications: '',
    allergies: '',
    notes: '',
    contraindicationsSignature: '',
  }
}
